import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  Modal,
  NumberInput,
  PasswordInput,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import { api, call, type Schemas } from "../api/client";
import { useMe } from "../lib/auth";

export function SettingsPage() {
  return (
    <Stack>
      <Title order={3}>Settings</Title>
      <Account />
      <ApiTokens />
      <ChangePassword />
    </Stack>
  );
}

function Account() {
  const me = useMe();
  const queryClient = useQueryClient();
  const zones = useMemo(() => Intl.supportedValuesOf("timeZone"), []);
  const save = useMutation({
    mutationFn: (body: Schemas["UserUpdate"]) => call(api.PATCH("/api/v1/me", { body })),
    onSuccess: () => {
      queryClient.invalidateQueries();
      notifications.show({ color: "teal", message: "Settings saved" });
    },
  });
  return (
    <Card withBorder>
      <Stack>
        <Text fw={600}>Account</Text>
        <Text size="sm">
          Signed in as <b>{me.data?.username}</b>
        </Text>
        <Select
          label="Timezone"
          description="Decides which day 'today' is, for you and your agents"
          data={zones}
          value={me.data?.timezone ?? null}
          onChange={(v) => v && save.mutate({ timezone: v })}
          searchable
          allowDeselect={false}
          maw={360}
        />
        <div>
          <Text size="sm" fw={500}>
            Height and weight units
          </Text>
          <SegmentedControl
            value={me.data?.units ?? "metric"}
            onChange={(v) => save.mutate({ units: v as Schemas["Units"] })}
            data={[
              { value: "metric", label: "Metric (kg, cm)" },
              { value: "imperial", label: "Imperial (lb, ft/in)" },
            ]}
            aria-label="Height and weight units"
          />
        </div>
      </Stack>
    </Card>
  );
}

function ApiTokens() {
  const queryClient = useQueryClient();
  const tokens = useQuery({ queryKey: ["tokens"], queryFn: () => call(api.GET("/api/v1/tokens")) });
  const [name, setName] = useState("");
  const [scope, setScope] = useState<Schemas["TokenScope"]>("read_write");
  const [days, setDays] = useState<number | string>("");
  const [created, setCreated] = useState<Schemas["ApiTokenCreated"] | null>(null);

  const create = useMutation({
    mutationFn: () =>
      call(
        api.POST("/api/v1/tokens", {
          body: { name: name.trim(), scope, expires_in_days: days === "" ? null : Number(days) },
        }),
      ),
    onSuccess: (t) => {
      setCreated(t);
      setName("");
      queryClient.invalidateQueries({ queryKey: ["tokens"] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: number) =>
      call(api.DELETE("/api/v1/tokens/{token_id}", { params: { path: { token_id: id } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tokens"] }),
    meta: { errorTitle: "Couldn't revoke" },
  });
  const when = (d: string | null) => (d ? dayjs(d + "Z").format("D MMM YYYY") : "–");

  return (
    <Card withBorder>
      <Stack>
        <div>
          <Text fw={600}>API tokens for agents</Text>
          <Text size="sm" c="dimmed">
            Give an agent a token and the API spec at <Code>/api/v1/openapi.json</Code>. Tokens can read
            and log data but can't change your password or create other tokens.
          </Text>
        </div>
        <Group align="flex-end" wrap="wrap">
          <TextInput label="Name" placeholder="e.g. Muse" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Select label="Access" data={[{ value: "read_write", label: "Read and write" }, { value: "read", label: "Read only" }]} value={scope} onChange={(v) => v && setScope(v as Schemas["TokenScope"])} allowDeselect={false} w={160} />
          <NumberInput label="Expires after (days)" placeholder="never" min={1} max={3650} value={days} onChange={setDays} w={160} hideControls />
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!name.trim()}>
            Create token
          </Button>
        </Group>
        {tokens.data && tokens.data.length > 0 && (
          <Table.ScrollContainer minWidth={560}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Token</Table.Th>
                  <Table.Th>Access</Table.Th>
                  <Table.Th>Last used</Table.Th>
                  <Table.Th>Expires</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {tokens.data.map((t) => (
                  <Table.Tr key={t.id} c={t.revoked_at ? "dimmed" : undefined}>
                    <Table.Td>{t.name}</Table.Td>
                    <Table.Td>
                      <Code>{t.prefix}…</Code>
                    </Table.Td>
                    <Table.Td>{t.scope === "read" ? "read only" : "read & write"}</Table.Td>
                    <Table.Td>{when(t.last_used_at)}</Table.Td>
                    <Table.Td>{t.expires_at ? when(t.expires_at) : "never"}</Table.Td>
                    <Table.Td ta="right">
                      {t.revoked_at ? (
                        <Badge color="gray" variant="light">revoked</Badge>
                      ) : (
                        <Button size="compact-xs" color="red" variant="subtle" onClick={() => revoke.mutate(t.id)}>
                          Revoke
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
      <Modal opened={created !== null} onClose={() => setCreated(null)} title={`Token "${created?.name}" created`}>
        <Stack>
          <Alert color="yellow">Copy it now. It won't be shown again.</Alert>
          <Code block style={{ wordBreak: "break-all" }} data-testid="new-token">
            {created?.token}
          </Code>
          <CopyButton value={created?.token ?? ""}>
            {({ copied, copy }) => (
              <Button onClick={copy} color={copied ? "teal" : undefined}>
                {copied ? "Copied" : "Copy token"}
              </Button>
            )}
          </CopyButton>
        </Stack>
      </Modal>
    </Card>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const change = useMutation({
    mutationFn: () =>
      call(api.POST("/api/v1/me/password", { body: { current_password: current, new_password: next } })),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      notifications.show({ color: "teal", message: "Password changed. Other sessions were signed out." });
    },
    meta: { errorTitle: "Couldn't change the password" },
  });
  return (
    <Card withBorder>
      <Stack maw={360}>
        <Text fw={600}>Change password</Text>
        <PasswordInput label="Current password" value={current} onChange={(e) => setCurrent(e.currentTarget.value)} autoComplete="current-password" />
        <PasswordInput label="New password" description="At least 10 characters" value={next} onChange={(e) => setNext(e.currentTarget.value)} autoComplete="new-password" />
        <Button w="fit-content" onClick={() => change.mutate()} loading={change.isPending} disabled={!current || next.length < 10}>
          Change password
        </Button>
      </Stack>
    </Card>
  );
}
